import { useState } from "react";
import { Clock12 } from "lucide-react";
import { MomentShell, SaveButton } from "./MomentShell";
import { Chips, Field, WHEN, HELPED, DIFFICULTY, inputCls } from "./Chips";

const STARTED = [
  { value: "done", label: "Già completata" },
  { value: "not_started", label: "Non ho ancora iniziato" },
  { value: "partial", label: "Iniziata, non finita" },
];

const REPLY = {
  done: "Bene: hai confermato di averla svolta. Stasera non dovrai confermarla di nuovo.",
  not_started: "Va bene, c'è ancora tempo. Se serve, puoi ridurla o cambiare momento. Stasera ti chiederò com'è andata.",
  partial: "Un inizio conta. Stasera ti chiederò se l'hai completata.",
};

const clean = (t) => (t || "").replace(/[.\s]+$/, "");

export function NoonStep({ plan, checkin, evening, time, onSave }) {
  const started = checkin?.started;
  const [reducing, setReducing] = useState(false);
  const [minimal, setMinimal] = useState(plan?.minimal || "");
  const [saving, setSaving] = useState(false);
  const closed = !!evening?.final;

  const saveReduce = async () => {
    setSaving(true);
    if (await onSave({ reduce: true, minimal: minimal.trim() })) setReducing(false);
    setSaving(false);
  };

  return (
    <MomentShell icon={Clock12} label="Mezzogiorno" time={time} title="A che punto sei con il tuo passo?" done={!!started} index={1} testid="moment-focus">
      {!plan ? (
        <p className="a2g-read mt-2 text-base text-[#B4B4BC]" data-testid="noon-no-plan">
          Stamattina non hai scelto un passo. Sceglilo nel Mattino qui sopra: bastano pochi secondi.
        </p>
      ) : closed ? (
        <p className="a2g-read mt-2 text-base text-[#B4B4BC]" data-testid="noon-closed">
          L'esito del passo è stato registrato alla Sera: lo trovi qui sotto.
        </p>
      ) : (
        <>
          <p className="a2g-read mt-2 text-base text-[#B4B4BC]" data-testid="noon-plan-reminder">
            Il tuo passo: <span className="font-medium text-[#F4F4F5]">{clean(plan.reduced && plan.minimal ? plan.minimal : plan.action)}</span>
            {plan.reduced && <span className="text-[#8A8E96]"> (versione ridotta di: {clean(plan.action)})</span>}
            {plan.when ? ` · ${plan.when.toLowerCase()}` : ""}
          </p>
          <Chips options={STARTED} value={started} onChange={(v) => v && onSave({ started: v })} testid="noon-started" />
          {started && <p className="a2g-read mt-4 text-base text-[#E8E4D8]" data-testid="noon-reply">{REPLY[started]}</p>}

          {started === "done" && (
            <Field label="Cosa ti ha aiutato? (facoltativo)">
              <Chips options={HELPED} value={checkin?.helped} onChange={(v) => onSave({ helped: v || "" })} testid="noon-helped" />
            </Field>
          )}

          {started && started !== "done" && (
            <>
              <Field label="Vuoi cambiare qualcosa? (facoltativo)">
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => { setMinimal(plan.minimal || ""); setReducing(!reducing); }} data-testid="noon-reduce-button" className={`rounded-xl border px-3 py-3 text-sm font-semibold ${plan.reduced ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4F4F5]" : "border-[#27272A] bg-[#0D0D0F] text-[#B4B4BC]"}`}>
                    {plan.reduced ? "Ridotta ✓" : "Riduco l'impegno"}
                  </button>
                  <button type="button" onClick={() => onSave({ reschedule: !checkin?.reschedule })} data-testid="noon-reschedule-button" className={`rounded-xl border px-3 py-3 text-sm font-semibold ${checkin?.reschedule ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4F4F5]" : "border-[#27272A] bg-[#0D0D0F] text-[#B4B4BC]"}`}>
                    {checkin?.reschedule ? "Momento cambiato ✓" : "Cambio momento"}
                  </button>
                </div>
              </Field>
              {reducing && (
                <div data-testid="noon-reduce-form">
                  <input value={minimal} onChange={(e) => setMinimal(e.target.value)} maxLength={200} placeholder="La versione più piccola, es. una pagina" className={inputCls} data-testid="noon-minimal-input" />
                  <SaveButton onClick={saveReduce} disabled={minimal.trim().length < 2} saving={saving} label="Usa questa versione" testid="noon-minimal-save" />
                  {plan.reduced && (
                    <button type="button" onClick={() => onSave({ reduce: false }).then(() => setReducing(false))} data-testid="noon-reduce-cancel" className="mt-2 text-sm text-[#8A8E96] underline">Torna alla versione iniziale</button>
                  )}
                </div>
              )}
              {plan.reduced && !reducing && (
                <p className="mt-3 rounded-xl border border-[#27272A] bg-[#0D0D0F] p-3.5 text-base text-[#F4F4F5]" data-testid="noon-minimal">
                  Nuova versione: {plan.minimal}. Non è ancora segnata come svolta.
                </p>
              )}
              {checkin?.reschedule && (
                <Field label="Quando la fai?">
                  <Chips options={WHEN} value={plan.when} onChange={(v) => v && onSave({ reschedule: true, when: v })} testid="noon-when" />
                </Field>
              )}
              <Field label="C'è un ostacolo? (facoltativo)">
                <Chips options={DIFFICULTY} value={checkin?.difficulty} onChange={(v) => onSave({ difficulty: v || "" })} testid="noon-difficulty" />
              </Field>
            </>
          )}
        </>
      )}
    </MomentShell>
  );
}
